import React, { useState } from 'react';
import {
    Button,
    makeStyles,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress
} from '@material-ui/core';
import { Edit, Image, Add } from '@material-ui/icons';
import { updatePrescription } from '../../../services/user.service';
import { upload } from '../../../services/storage.service';

const useStyles = makeStyles((theme) => ({
    tableContainer: {
        marginTop: theme.spacing(2),
        maxHeight: 400,
    },
    tableHeader: {
        backgroundColor: theme.palette.grey[100],
        fontWeight: 'bold',
    },
    actionCell: {
        display: 'flex',
        gap: theme.spacing(3),
    },
    noteCell: {
        maxWidth: 300,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    dialogTitle: {
        borderBottom: '1px solid #E0E0E0',
        paddingBottom: theme.spacing(2),
    },
    mainDialogContent: {
        padding: theme.spacing(3),
        minWidth: 800,
    },
    secondaryDialogContent: {
        padding: theme.spacing(3),
        minWidth: 500,
    },
    uploadButton: {
        marginBottom: theme.spacing(2),
    },
    imageDialog: {
        maxWidth: '90vw',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    imagePreview: {
        maxWidth: '100%',
        maxHeight: '70vh',
        marginBottom: theme.spacing(2),
    },
    editDialogContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(4),
    },
    formRow: {
        display: 'flex',
        gap: theme.spacing(2),
        alignItems: 'center',
        marginBottom: theme.spacing(3),
    },
    fileInput: {
        display: 'none',
    },
    viewButton: {
        textTransform: 'none',
        padding: '6px 12px',
        marginRight: theme.spacing(2),
    },
    editButton: {
        textTransform: 'none',
        padding: '6px 12px',
    },
    noteField: {
        marginTop: theme.spacing(2),
    },
    fileName: {
        marginLeft: theme.spacing(2),
        fontWeight: 500,
    },
    uploadStatus: {
        marginLeft: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    errorText: {
        color: theme.palette.error.main,
        marginTop: theme.spacing(1),
    }
}));

const PrescriptionDetails = ({ data }) => {
    const { program_id, patient_id, prescription, registration_date } = data || {};

    const classes = useStyles();
    const [openMainModal, setOpenMainModal] = useState(false);
    const [openSecondaryModal, setOpenSecondaryModal] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [editPrescription, setEditPrescription] = useState(null);
    const [newPrescription, setNewPrescription] = useState({
        date: new Date().toISOString().slice(0, 16),
        note: '',
        filepath: '',
        webview_path: ''
    });
    const [fileToUpload, setFileToUpload] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [originalFile, setOriginalFile] = useState(null);

    // Parse prescriptions from data
    const parsePrescriptions = () => {
        try {
            if (!prescription) return [];
            if (Array.isArray(prescription)) return prescription;
            return JSON.parse(prescription) || [];
        } catch (error) {
            console.error('Error parsing prescriptions:', error);
            return [];
        }
    };

    const prescriptions = parsePrescriptions();

    // Modal handlers
    const handleOpenMainModal = () => setOpenMainModal(true);
    const handleCloseMainModal = () => {
        setOpenMainModal(false);
        setImagePreview(null);
        setEditPrescription(null);
    };

    const handleCloseSecondaryModal = () => {
        setOpenSecondaryModal(false);
        setImagePreview(null);
        setEditPrescription(null);
        setFileToUpload(null);
        setUploadError(null);
    };

    // Image viewing
    const handleViewImage = (url) => {
        if (url) {
            setImagePreview(url);
            setOpenSecondaryModal(true);
        }
    };

    const handleEdit = (prescription) => {

        const baseDate = program_id?.includes("my-onecare") && prescription.id === "prescription-0"
            ? (prescription.date || registration_date)
            : prescription.date;

        setOriginalFile({
            filepath: prescription.filepath,
            webview_path: prescription.webview_path
        });
        setEditPrescription({
            ...prescription,
            date: baseDate ? new Date(baseDate).toISOString().slice(0, 16) : ""
        });
        setOpenSecondaryModal(true);
    };

    const handleUpload = () => {
        setEditPrescription(null);
        setNewPrescription({
            date: new Date().toISOString().slice(0, 16),
            note: '',
            filepath: '',
            webview_path: ''
        });
        setOpenSecondaryModal(true);
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadError(null);
        setIsUploading(true);

        if (file.size > 5 * 1024 * 1024) {
            setUploadError('File too large (max 5MB)');
            setIsUploading(false);
            return;
        }

        try {
            const { result } = await upload(file, program_id);

            if (!result?.url) {
                throw new Error('Upload failed - no URL returned');
            }

            setFileToUpload({ file, url: result.url });

            if (editPrescription) {
                setEditPrescription(prev => ({
                    ...prev,
                    filepath: file.name,
                    webview_path: result.url
                }));
            } else {
                setNewPrescription(prev => ({
                    ...prev,
                    filepath: file.name,
                    webview_path: result.url
                }));
            }
        } catch (error) {
            console.error('Upload failed:', error);
            setUploadError(error.message || 'File upload failed');
        } finally {
            setIsUploading(false);
        }
    };

    // Form field handlers
    const handleDateChange = (e) => {
        const value = e.target.value;
        if (editPrescription) {
            setEditPrescription(prev => ({ ...prev, date: value }));
        } else {
            setNewPrescription(prev => ({ ...prev, date: value }));
        }
    };

    const handleNoteChange = (e) => {
        const value = e.target.value;
        if (editPrescription) {
            setEditPrescription(prev => ({ ...prev, note: value }));
        } else {
            setNewPrescription(prev => ({ ...prev, note: value }));
        }
    };

    const onSubmit = async () => {
        try {
            if (uploadError) {
                alert(uploadError);
                return;
            }
            setIsSaving(true);
            const prescriptionPayload = {
                id: editPrescription?.id || `prescription-${Date.now()}`,
                date: editPrescription?.date || newPrescription.date,
                note: editPrescription?.note || newPrescription.note || '',
                filepath: fileToUpload?.file.name ||
                    (editPrescription?.filepath && !fileToUpload ? editPrescription.filepath : ''),
                webview_path: fileToUpload?.url ||
                    (editPrescription?.webview_path && !fileToUpload ? editPrescription.webview_path : '')
            };


            if (!prescriptionPayload.date) {
                throw new Error('Date is required');
            }

            if (editPrescription && !prescriptionPayload.filepath) {
                throw new Error('File is required for prescription');
            }

            const response = await updatePrescription({
                program_id,
                patient_id,
                prescription: prescriptionPayload
            });

            if (response?.error) {
                throw new Error(response.error.message || 'Backend error occurred');
            }

            if (response?.inputs) {
                const errorMessages = Object.entries(response.inputs)
                    .filter(([_, value]) => value)
                    .map(([key, value]) => `${key}: ${value}`)
                    .join('\n');
                throw new Error(errorMessages);
            }

            alert(`Prescription ${editPrescription ? 'updated' : 'added'} successfully!`);
            window.location.reload();
            handleCloseSecondaryModal();

            if (!editPrescription) {
                setNewPrescription({
                    date: new Date().toISOString().slice(0, 16),
                    note: '',
                    filepath: '',
                    webview_path: ''
                });
            }
            setFileToUpload(null);
            setOriginalFile(null);

        } catch (error) {
            console.error('Submission failed:', error);

            let errorMessage = 'Failed to save prescription';
            if (error.message.includes('Network Error')) {
                errorMessage = 'Network error - please check your connection';
            } else if (error.message) {
                errorMessage = error.message;
            }

            alert(errorMessage);

            // Rollback file changes if edit failed
            if (editPrescription && fileToUpload) {
                setEditPrescription(prev => ({
                    ...prev,
                    filepath: originalFile?.filepath || prev.filepath,
                    webview_path: originalFile?.webview_path || prev.webview_path
                }));
                setFileToUpload(null);
            }
        } finally {
            setIsSaving(false);
        }
    };

    // Date formatting helper
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return isNaN(date.getTime()) ? dateString : date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };

    // Dialog content renderers
    const renderImageDialog = () => (
        <div className={classes.imageDialog}>
            <img
                src={imagePreview}
                alt="Prescription"
                className={classes.imagePreview}
                onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'path/to/placeholder-image.png';
                }}
            />
        </div>
    );

    const renderEditDialog = () => (
        <div className={classes.editDialogContent}>
            <div className={classes.formRow}>
                <TextField
                    label='Date'
                    type="datetime-local"
                    value={editPrescription?.date || newPrescription.date}
                    onChange={handleDateChange}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    required
                />
            </div>

            <div className={classes.formRow}>
                <input
                    accept="image/*"
                    className={classes.fileInput}
                    id="prescription-upload"
                    type="file"
                    onChange={handleFileChange}
                    disabled={isUploading}
                />
                <label htmlFor="prescription-upload">
                    <Button
                        variant="contained"
                        component="span"
                        color="primary"
                        disabled={isUploading}
                        startIcon={isUploading ? <CircularProgress size={20} /> : null}
                    >
                        {isUploading ? 'Uploading...' :
                            (editPrescription?.filepath ? 'Change File' : 'Upload File')}
                    </Button>
                </label>
                <div className={classes.uploadStatus}>
                    <Typography className={classes.fileName}>
                        {editPrescription?.filepath || newPrescription.filepath || 'No file selected'}
                    </Typography>
                </div>
                {uploadError && (
                    <Typography className={classes.errorText}>
                        {uploadError}
                    </Typography>
                )}
            </div>

            <TextField
                label="Notes"
                placeholder="Add notes here"
                multiline
                rows={4}
                value={editPrescription?.note || newPrescription.note}
                onChange={handleNoteChange}
                variant="outlined"
                fullWidth
            />
        </div>
    );

    const renderMainContent = () => (
        <>
            <Button
                variant="contained"
                color="primary"
                onClick={handleUpload}
                className={classes.uploadButton}
                startIcon={<Add />}
            >
                Add Prescription
            </Button>

            <Typography variant="subtitle1" gutterBottom>
                {prescriptions.length} prescription(s) found
            </Typography>

            <TableContainer component={Paper} className={classes.tableContainer}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.tableHeader}>File</TableCell>
                            <TableCell className={classes.tableHeader}>Date</TableCell>
                            <TableCell className={classes.tableHeader}>Notes</TableCell>
                            <TableCell className={classes.tableHeader}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {prescriptions.length > 0 ? (
                            prescriptions.map((prescription, index) => (
                                <TableRow key={`${prescription.id || index}-${index}`}>
                                    <TableCell>{prescription.filepath || `Prescription ${index + 1}`}</TableCell>
                                    <TableCell>
                                        {formatDate(
                                            program_id?.includes("my-onecare")
                                                ? (prescription.date || (prescription.id === "prescription-0" ? registration_date : null))
                                                : prescription.date
                                        )}
                                    </TableCell>
                                    <TableCell className={classes.noteCell}>
                                        {prescription.note || ''}
                                    </TableCell>
                                    <TableCell>
                                        <div className={classes.actionCell}>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                className={classes.viewButton}
                                                onClick={() => handleViewImage(prescription.webview_path)}
                                                disabled={!prescription.webview_path}
                                                startIcon={<Image />}
                                            >
                                                View
                                            </Button>

                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                className={classes.editButton}
                                                onClick={() => handleEdit(prescription)}
                                                startIcon={<Edit />}
                                            >
                                                Edit
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    No prescriptions available
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );

    const getSecondaryDialogContent = () => {
        if (imagePreview) return renderImageDialog();
        return renderEditDialog();
    };

    const getSecondaryDialogTitle = () => {
        if (imagePreview) return 'Prescription Image';
        return editPrescription ? 'Edit Prescription' : 'Add Prescription';
    };

    return (
        <>
            <Button
                variant="contained"
                onClick={handleOpenMainModal}
                style={{ marginLeft: 8 }}
            >
                View Prescriptions ({prescriptions.length})
            </Button>

            {/* Main Dialog */}
            <Dialog
                open={openMainModal}
                onClose={handleCloseMainModal}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle className={classes.dialogTitle}>
                    Prescription Details
                </DialogTitle>
                <DialogContent className={classes.mainDialogContent}>
                    {renderMainContent()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseMainModal} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Secondary Dialog */}
            <Dialog
                open={openSecondaryModal}
                onClose={handleCloseSecondaryModal}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle className={classes.dialogTitle}>
                    {getSecondaryDialogTitle()}
                </DialogTitle>
                <DialogContent className={classes.secondaryDialogContent}>
                    {getSecondaryDialogContent()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseSecondaryModal} color="primary" disabled={isSaving}>
                        Cancel
                    </Button>
                    {!imagePreview &&
                        <Button
                            onClick={onSubmit}
                            color="primary"
                            variant="contained"
                            startIcon={isSaving ? <CircularProgress size={20} /> : null}
                            disabled={
                                isSaving ||
                                isUploading ||
                                !(editPrescription?.filepath || newPrescription.filepath) ||
                                !(editPrescription?.date || newPrescription.date)
                            }
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PrescriptionDetails;